import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { MarkdownContent } from "../components/MarkdownContent";
import { usePageMeta, SITE_NAME } from "../hooks/usePageMeta";
import { blogService } from "../services/blogService";
import type { Blog } from "../types/blog";

export function BlogView() {
    const { slug } = useParams();
    const [blog, setBlog] = useState<Blog | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const jsonLd = useMemo(() => {
        if (!blog) return null;
        return {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: blog.title,
            description: blog.description || undefined,
            image: blog.cover_image || blog.thumbnail_image || undefined,
            datePublished: blog.created_at,
            dateModified: blog.updated_at,
            author: {
                "@type": "Person",
                name: "Shonei",
            },
            publisher: {
                "@type": "Organization",
                name: SITE_NAME,
            },
            mainEntityOfPage: {
                "@type": "WebPage",
                "@id": `/blog/${blog.slug}`,
            },
        };
    }, [blog]);

    usePageMeta(
        blog
            ? {
                  title: blog.title,
                  description: blog.description || undefined,
                  path: `/blog/${blog.slug}`,
                  image: blog.cover_image || blog.thumbnail_image,
                  type: "article",
                  jsonLd,
              }
            : {
                  title: error ? "Not found" : undefined,
                  path: slug ? `/blog/${slug}` : undefined,
                  noindex: Boolean(error),
              }
    );

    useEffect(() => {
        if (!slug) return;

        const fetchBlog = async () => {
            setLoading(true);
            try {
                // Try fetching by slug directly if the API supports it
                // We're casting slug to string | number which satisfies the type
                // In a real app, we might need a specific getBySlug endpoint or 
                // fallback to fetching all and finding.
                const data = await blogService.getById(slug);
                setBlog(data);
            } catch (err) {
                console.error("Failed to fetch blog by slug", err);
                // Fallback: This is a hack because we don't know if backend supports slug lookup
                // In production, we'd want a dedicated endpoint.
                try {
                    const allBlogs = await blogService.getAll();
                    const found = allBlogs.find(b => b.slug === slug);
                    if (found) {
                        setBlog(found);
                    } else {
                        setError("Blog not found");
                    }
                } catch (fallbackErr) {
                    setError("Failed to load blog");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchBlog();
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen bg-base-100 flex flex-col">
                <Navbar />
                <div className="flex-1 flex justify-center items-center">
                    <span className="loading loading-spinner loading-lg text-primary"></span>
                </div>
            </div>
        );
    }

    if (error || !blog) {
        return (
            <div className="min-h-screen bg-base-100 flex flex-col">
                <Navbar />
                <div className="flex-1 flex justify-center items-center">
                    <div className="alert alert-error max-w-md">
                        <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <span>{error || "Blog not found"}</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-base-100 text-base-content font-sans flex flex-col">
            <Navbar title={blog.title} />

            <main className="flex-1 w-full max-w-4xl mx-auto px-4 py-8 md:py-12">
                <article className="prose prose-lg md:prose-xl max-w-none prose-headings:font-bold prose-h1:text-4xl prose-h1:mb-4 prose-img:rounded-2xl prose-img:shadow-lg prose-a:text-primary prose-a:hover:underline">

                    {/* Hero Header with Background Image */}
                    <header
                        className="relative mb-12 not-prose rounded-3xl overflow-hidden shadow-2xl bg-base-300 min-h-[400px] flex flex-col justify-end"
                        style={{
                            backgroundImage: blog.cover_image ? `url(${blog.cover_image})` : undefined,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }}
                    >
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

                        {/* Content */}
                        <div className="relative z-10 p-8 md:p-12 text-white">
                            <div className="flex flex-wrap gap-2 mb-4">
                                {blog.tags && Object.entries(blog.tags).map(([key, value]) => (
                                    <span key={key} className="badge badge-primary border-none text-primary-content">{key}: {value}</span>
                                ))}
                            </div>
                            <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4 leading-tight text-white shadow-sm">
                                {blog.title}
                            </h1>
                            {blog.description && (
                                <p className="text-xl md:text-2xl text-white/90 leading-relaxed font-light max-w-2xl">
                                    {blog.description}
                                </p>
                            )}
                            <div className="flex items-center gap-4 mt-6 text-sm font-medium text-white/70 uppercase tracking-widest">
                                <span>{new Date(blog.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                            </div>
                        </div>
                    </header>

                    {/* Content Body */}
                    <div className="prose-pre:bg-base-300 prose-pre:text-base-content prose-pre:border prose-pre:border-base-content/5 prose-code:bg-base-200 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none [&_pre_code]:bg-transparent [&_pre_code]:p-0 font-serif px-2 md:px-0">
                        <MarkdownContent>{blog.content || ""}</MarkdownContent>
                    </div>
                </article>
            </main>

            <Footer />
        </div>
    );
}
